import { Box, Dialog, Switch } from '@mui/material';
import { styled } from '@mui/system';
import CloseIcon from '@mui/icons-material/Close';
import PropTypes from 'prop-types';
import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { updateUserInfo } from '../redux/features/usersSlice';
import { DIVIDER, MUTED, SANS_STACK, TEXT_DARK } from './common/tokens';
import {
  DIALOG_PAPER_SX, DialogCloseButton, DialogFooter, DialogHeader,
  DialogPrimaryButton, DialogTitle,
} from './common/dialog/primitives';

const SettingRow = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 16,
  padding: '12px 0',
  '& + &': { borderTop: `1px solid ${DIVIDER}` },
});

const SettingLabel = styled('div')({
  fontFamily: SANS_STACK,
  fontSize: 14.5,
  fontWeight: 600,
  color: TEXT_DARK,
});

const SettingDescription = styled('div')({
  fontFamily: SANS_STACK,
  fontSize: 12.5,
  color: MUTED,
  marginTop: 2,
});

function SettingsDialog({ open, onClose }) {
  const dispatch = useDispatch();
  const darkMode = useSelector((state) => state.users.userInfo?.darkMode || false);
  const anyLanguage = useSelector((state) => state.users.userInfo?.anyLanguage || false);

  const setSetting = (patch) => dispatch(updateUserInfo(patch));

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{ elevation: 0, sx: DIALOG_PAPER_SX }}
    >
      <DialogHeader>
        <DialogTitle>Settings</DialogTitle>
        <DialogCloseButton onClick={onClose} aria-label="Close">
          <CloseIcon />
        </DialogCloseButton>
      </DialogHeader>

      <Box sx={{ px: 2, pb: 1 }}>
        <SettingRow>
          <Box>
            <SettingLabel>Dark mode</SettingLabel>
            <SettingDescription>Use a dark color theme across the app.</SettingDescription>
          </Box>
          <Switch
            checked={darkMode}
            onChange={() => setSetting({ darkMode: !darkMode })}
            inputProps={{ 'aria-label': 'Dark mode' }}
          />
        </SettingRow>
        <SettingRow>
          <Box>
            <SettingLabel>Any language</SettingLabel>
            <SettingDescription>Include content in all languages, not just English.</SettingDescription>
          </Box>
          <Switch
            checked={anyLanguage}
            onChange={() => setSetting({ anyLanguage: !anyLanguage })}
            inputProps={{ 'aria-label': 'Any language' }}
          />
        </SettingRow>
      </Box>

      <DialogFooter sx={{ justifyContent: 'flex-end' }}>
        <DialogPrimaryButton disableElevation variant="contained" onClick={onClose}>
          Done
        </DialogPrimaryButton>
      </DialogFooter>
    </Dialog>
  );
}

SettingsDialog.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
};

SettingsDialog.defaultProps = {
  open: false,
};

export default SettingsDialog;
